import { Box, TextField } from '@atgfr8/atg-ui-tailwind';
import { Button, ToastType } from '@components';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import {
  calculateMileage,
  customerStore,
  orderStore,
  setOrderStore,
  updateLoadPropertyAtIndex,
} from '@store/orders';
import { RawStopLocationT } from '@typeDefinitions/stopTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { logError } from '@utils/errorLogging';
import {
  areArraysEqual,
  clampMileageValue,
  getActiveStopIndices,
  handleToast,
  preventHyphen,
} from '@utils/utils';
import { size } from 'lodash';
import { Component, createEffect, createMemo, createSignal } from 'solid-js';
import { produce } from 'solid-js/store';

import classes from './classes';

type StopsMileageProps = {
  tabIndex: number;
  disabled?: boolean;
};

const StopsMileage: Component<StopsMileageProps> = (props) => {
  const [loading, setLoading] = createSignal(false);
  const stopMileageEnabled = isFeatureFlagEnabled(FeatureFlags.StopMileage)();

  let isFirstRender = true;

  const stops = createMemo(() => {
    return orderStore.order.loads[orderStore.activeTab.index]?.stops?.filter(
      (stop) => {
        return stop.operationType !== 'Delete';
      },
    );
  });

  let prevStopLocations: RawStopLocationT[] = [];

  const stopLocations = createMemo(() => {
    const locations =
      stops()?.map((s) => {
        return {
          address1: s.address1,
          address2: s.address2,
          city: s.city,
          state: s.state,
          zip: s.zip,
          lat: s.lat,
          long: s.long,
        } as RawStopLocationT;
      }) ?? [];

    if (!areArraysEqual(prevStopLocations, locations)) {
      prevStopLocations = locations;
      return locations;
    }

    return prevStopLocations;
  });

  const activeIndex = createMemo(() => orderStore.activeTab.index);
  const activeTabType = createMemo(() => orderStore.activeTab.type);

  createEffect(() => {
    if (activeTabType() === 'load') {
      if (stopMileageEnabled && stopLocations().length > 0) {
        if (isFirstRender) {
          isFirstRender = false;
          return;
        }

        if (stopLocations().length <= 1) {
          setOrderStore(
            'order',
            'loads',
            activeIndex(),
            produce((draft) => {
              draft.practicalMileage = 0;
              draft.shortestMileage = 0;
            }),
          );
          return;
        }

        void getMileage();
      }
    }
  });

  const getMileage = async () => {
    if (orderStore.isReadOnly) {
      return;
    }

    if (!stops()) {
      return;
    }

    try {
      setLoading(true);
      const response = await calculateMileage(
        stops(),
        customerStore.customer.pcMilerVersion
          ? customerStore.customer.pcMilerVersion
          : null,
      );
      const activeStopIndices = getActiveStopIndices();
      const newStopList =
        orderStore.order.loads[orderStore.activeTab.index]?.stops ?? [];
      let distanceResponse:
        | {
            shortMiles: string | number;
            practicalMiles: string | number;
          }
        | undefined;

      for (const stopIndex of activeStopIndices) {
        const arrayIndex: number = activeStopIndices.indexOf(stopIndex);

        if (arrayIndex === activeStopIndices.length - 1) {
          setOrderStore(
            'order',
            'loads',
            orderStore.activeTab.index,
            'stops',
            stopIndex,
            produce((draft) => {
              draft.practicalMileageToNextStop = null;
              draft.shortestMileageToNextStop = null;
            }),
          );
          continue;
        }

        distanceResponse = await calculateMileage(
          [
            newStopList[stopIndex],
            newStopList[activeStopIndices[arrayIndex + 1]],
          ],
          customerStore.customer.pcMilerVersion
            ? customerStore.customer.pcMilerVersion
            : null,
        );

        const err = new Error('Observability for mileage');
        err.name = 'MileageObservability(StopsMileage)';

        logError(err.name, err, {
          shortMiles: distanceResponse?.shortMiles ?? null,
          practicalMiles: distanceResponse?.practicalMiles ?? null,
          coords: stops(),
          orderId: orderStore.order.id,
        });

        setOrderStore(
          'order',
          'loads',
          orderStore.activeTab.index,
          'stops',
          stopIndex,
          produce((draft) => {
            draft.practicalMileageToNextStop =
              distanceResponse?.practicalMiles as number;
            draft.shortestMileageToNextStop =
              distanceResponse?.shortMiles as number;
          }),
        );
      }

      setOrderStore(
        'order',
        'loads',
        orderStore.activeTab.index,
        produce((draft) => {
          const opType = draft.operationType === 'Insert' ? 'Insert' : 'Update';
          draft.practicalMileage = response?.practicalMiles as number;
          draft.shortestMileage = response?.shortMiles as number;
          draft.operationType = opType;
        }),
      );
    } catch (error) {
      handleToast(ToastType.Error, 'Error while getting mileage');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box justifyContent="end" gap={2} class="flex flex-row mb-[16px]">
      <TextField
        placeholder="Practical"
        dataTestId="practical-textInput"
        value={orderStore.order.loads[props.tabIndex]?.practicalMileage ?? ''}
        onChange={(value: unknown) => {
          updateLoadPropertyAtIndex({
            practicalMileage: clampMileageValue(value),
          });
        }}
        class={classes.stopsMileageTextInput}
        label="Practical"
        type="number"
        disabled={props.disabled}
        onKeyDown={preventHyphen}
      />
      <TextField
        placeholder="Shortest"
        dataTestId="shortest-textInput"
        value={orderStore.order.loads[props.tabIndex]?.shortestMileage ?? ''}
        onChange={(value: unknown) => {
          updateLoadPropertyAtIndex({
            shortestMileage: clampMileageValue(value),
          });
        }}
        class={classes.stopsMileageTextInput}
        label="Shortest"
        type="number"
        disabled={props.disabled}
        onKeyDown={preventHyphen}
      />
      <Button
        label="Mileage"
        color="info"
        onClick={getMileage}
        isLoading={loading()}
        disabled={
          loading() ||
          size(
            orderStore.order.loads[props.tabIndex]?.stops?.filter(
              (stop) => stop.operationType !== 'Delete',
            ),
          ) < 2 ||
          props.disabled
        }
        dataTestId={formatDataTestId('Mileage', 'button')}
      />
    </Box>
  );
};

export default StopsMileage;
